/**
 * The browser half of `@repo/observability`.
 *
 * Every React app had its own copy of this: the same integrations, the same
 * router instrumentation, the same error hooks. Copies drift, and the scrubbing
 * on `beforeSend` is not something an app should be able to forget.
 */

import { scrubEvent } from "@repo/observability/scrub";
import * as Sentry from "@sentry/react";
import React from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router";

export interface BrowserTelemetryOptions {
  /** Empty or absent disables reporting entirely. */
  dsn?: string;
  /** Deployment name, e.g. `import.meta.env.MODE` from the calling app. */
  environment?: string;
  /**
   * Origins that receive a `sentry-trace` header. Omit unless the API is on
   * another origin: the SDK already covers same-origin and localhost.
   */
  tracePropagationTargets?: (string | RegExp)[];
  tracesSampleRate?: number;
  /** Router navigation spans. Off for an app that does not use react-router. */
  instrumentRouter?: boolean;
}

/**
 * Set by `initBrowserTelemetry`. `wrapReactRouterRouting` hands back `Routes`
 * untouched unless the tracing integration's `setup` has already stored the
 * router hooks, and that runs inside `Sentry.init` — so the wrap cannot sit at
 * module scope, where it is evaluated on import and always loses the race.
 */
let instrumentedRoutes: typeof Routes | undefined;

/** Initialize the browser SDK. A missing DSN makes every Sentry call a no-op. */
export function initBrowserTelemetry(options: BrowserTelemetryOptions): void {
  if (!options.dsn) {
    return;
  }

  Sentry.init({
    dsn: options.dsn,
    environment: options.environment,
    enableLogs: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
      ...(options.instrumentRouter === false
        ? []
        : [
            Sentry.reactRouterBrowserTracingIntegration({
              useEffect: React.useEffect,
              useLocation,
              useNavigationType,
              createRoutesFromChildren,
              matchRoutes,
            }),
          ]),
    ],
    ...(options.tracePropagationTargets
      ? { tracePropagationTargets: options.tracePropagationTargets }
      : {}),
    tracesSampleRate: options.tracesSampleRate ?? 0.1,
    // A breadcrumb URL, a console line and an error message all leave here, and
    // any of them can carry a token. This is the last point they pass through.
    beforeSend: scrubEvent,
    beforeSendLog: scrubEvent,
  });

  if (options.instrumentRouter !== false) {
    instrumentedRoutes = Sentry.wrapReactRouterRouting(Routes);
  }
}

/** `Routes` instrumented for tracing, so a page transition becomes a navigation span. */
export const SentryRoutes: typeof Routes = props =>
  React.createElement(instrumentedRoutes ?? Routes, props);

/** Passed to `createRoot` so React's own error paths reach Sentry. */
export const sentryHooks = {
  // Thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  // Caught by an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // React recovered on its own.
  onRecoverableError: Sentry.reactErrorHandler(),
};
